import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  DeleteMePreflightResponseSchema,
  DeleteMeRequestSchema,
  DeleteMeResponseSchema,
  type DeleteMePreflightResponse,
  type DeleteMeRequest,
  type DeleteMeResponse,
} from '@repo/api-client/dtos';
import { useMutation, useQuery } from '@tanstack/react-query';

import { getJSON } from '../internal/http';
import { mintIdempotencyKey, withIdempotencyKey } from '../internal/http';

/**
 * `GET /v1/users/me/delete-preflight` — read-only inspection of which
 * orgs would block / cascade / survive a delete-account attempt. The
 * mobile screen fires this on mount so the UI can branch into the
 * "resolve these workspaces first" view without round-tripping through
 * a 409.
 *
 * `staleTime: 0` because the data is the user's exact membership
 * shape *right now* — between two visits to the screen they may have
 * archived an org, transferred ownership, or accepted an invite, and
 * any of those flips the screen's branch.
 */
export function useDeleteMePreflight(options: { enabled?: boolean } = {}) {
  const http = useHttpClient();
  const enabled = options.enabled ?? true;

  const {
    data,
    error,
    isPending: loading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.user.deletePreflight(),
    queryFn: () =>
      getJSON(http, '/users/me/delete-preflight', DeleteMePreflightResponseSchema),
    enabled,
    staleTime: 0,
    gcTime: 30 * 1000,
  });

  return {
    preflight: data as DeleteMePreflightResponse | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * `DELETE /v1/users/me` — block-until-resolved account deletion.
 *
 * The hook deliberately stays scoped to the network round-trip + the
 * parsed envelope. The screen owns the post-success choreography
 * (clearing the SessionProvider, routing back to /welcome) because
 * different entry points (settings vs. last-step of an onboarding
 * abort, etc.) need different navigation.
 *
 * Pre-flight validation lives client-side too so an obviously-bad
 * submit (e.g. typing nothing in the confirmation field) doesn't
 * burn a per-user-throttle slot. The schema mirrors the server's
 * `DeleteMeSchema`.
 *
 * Conflict handling: if the user is the sole owner of a multi-member
 * org, the server returns a 409 surfaced as an `AppError` with
 * `code === 'OWNER_HAS_SHARED_ORGS'` and `meta.blockingOrgs`. The
 * caller pattern-matches on the code in its `onError` handler.
 */
export function useDeleteMe() {
  const http = useHttpClient();

  const mutation = useMutation({
    mutationFn: async (input: DeleteMeRequest): Promise<DeleteMeResponse> => {
      const body = DeleteMeRequestSchema.parse(input);
      // axios's `delete` accepts a body via `config.data` — needed
      // here so the confirmation email rides along the wire. The
      // idempotency-key header is supplied for parity with our other
      // mutations: replays of the same DELETE on a 5xx network blip
      // resolve to the same outcome (the user-row is gone or it
      // isn't), which the server's `IdempotencyService` cache
      // confirms within the 24h window.
      const response = await http.delete(
        '/users/me',
        withIdempotencyKey(mintIdempotencyKey(), { data: body }),
      );
      return DeleteMeResponseSchema.parse(response.data);
    },
  });

  return {
    deleteMe: mutation.mutateAsync as (
      input: DeleteMeRequest,
    ) => Promise<DeleteMeResponse>,
    loading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
